'use client';

import { Flex, Icon, Text, chakra } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { LensTag } from '@/types/Product';
import { LENS_TAG_ORDER, LENS_TAGS } from '@/config/lensTags';

const strong = { base: '#0A1B2B', _dark: '#E2EFF5' };

/**
 * Tags da lente da cor selecionada (Polarizado/Fotocromático/Espelhado).
 * A explicação abre ao clicar/tocar no "i" — num telemóvel não há "passar o
 * rato por cima", por isso não se usa tooltip de hover.
 */
const LensTags = ({ tags }: { tags?: LensTag[] }) => {
  const [openTag, setOpenTag] = useState<LensTag | null>(null);

  const visible = LENS_TAG_ORDER.filter((tag) => tags?.includes(tag));

  // Ao mudar de cor, fecha a explicação que estava aberta.
  const tagsKey = visible.join(',');
  useEffect(() => {
    setOpenTag(null);
  }, [tagsKey]);

  if (visible.length === 0) return null;

  return (
    <Flex flexDir="column" gap="8px">
      <Flex flexWrap="wrap" gap="8px 18px">
        {visible.map((tag) => {
          const { letter, label } = LENS_TAGS[tag];
          const isOpen = openTag === tag;
          return (
            <Flex key={tag} align="center" gap="8px">
              <Flex
                aria-hidden
                w="24px"
                h="24px"
                flexShrink={0}
                align="center"
                justify="center"
                borderRadius="full"
                border="1.5px solid"
                borderColor={strong}
                color={strong}
                fontFamily="Georgia, 'Times New Roman', serif"
                fontStyle="italic"
                fontWeight="700"
                fontSize="14px"
                lineHeight="1"
              >
                {letter}
              </Flex>
              <Text fontSize="14px" fontWeight="600" color={strong}>
                {label}
              </Text>
              <chakra.button
                type="button"
                aria-label={`O que é ${label.toLowerCase()}?`}
                aria-expanded={isOpen}
                onClick={() => setOpenTag(isOpen ? null : tag)}
                display="flex"
                alignItems="center"
                color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.5)' }}
                _hover={{ color: '#00B0CA' }}
                cursor="pointer"
              >
                <Icon w="15px" h="15px">
                  <FiInfo />
                </Icon>
              </chakra.button>
            </Flex>
          );
        })}
      </Flex>

      {openTag && (
        <Flex
          role="note"
          align="flex-start"
          gap="12px"
          p="12px 14px"
          borderRadius="8px"
          bg="#3A3F45"
          color="#FFFFFF"
        >
          <Text flex="1" fontSize="13px" lineHeight="1.45">
            {LENS_TAGS[openTag].description}
          </Text>
          <chakra.button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpenTag(null)}
            display="flex"
            color="rgba(255,255,255,0.7)"
            _hover={{ color: '#FFFFFF' }}
            cursor="pointer"
          >
            <Icon w="16px" h="16px">
              <FiX />
            </Icon>
          </chakra.button>
        </Flex>
      )}
    </Flex>
  );
};

export default LensTags;
